// Password Utility Functions
// Centralized password generation and validation utilities

/**
 * Generate a secure temporary password
 * @param {number} length - Length of the password (default: 12)
 * @returns {string} Generated password
 */
export const generateTemporaryPassword = (length = 12) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
  let password = ''
  
  // Ensure at least one character from each category
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const symbols = '!@#$%^&*'
  
  // Add one character from each category
  password += uppercase.charAt(Math.floor(Math.random() * uppercase.length))
  password += lowercase.charAt(Math.floor(Math.random() * lowercase.length))
  password += numbers.charAt(Math.floor(Math.random() * numbers.length))
  password += symbols.charAt(Math.floor(Math.random() * symbols.length))
  
  // Fill the rest with random characters
  for (let i = 4; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('')
}

/**
 * Generate a simple temporary password (numbers only)
 * @param {number} length - Length of the password (default: 8)
 * @returns {string} Generated numeric password
 */
export const generateSimpleTemporaryPassword = (length = 8) => {
  const numbers = '0123456789'
  let password = ''
  
  for (let i = 0; i < length; i++) {
    password += numbers.charAt(Math.floor(Math.random() * numbers.length))
  }
  
  return password
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} Validation result with score and feedback
 */
export const validatePasswordStrength = (password) => {
  const result = {
    score: 0,
    feedback: [],
    isValid: false
  }
  
  if (!password) {
    result.feedback.push('Password is required')
    return result
  }
  
  if (password.length < 8) {
    result.feedback.push('Password must be at least 8 characters long')
  } else {
    result.score += 1
  }
  
  if (password.length >= 12) {
    result.score += 1
  }
  
  if (/[a-z]/.test(password)) {
    result.score += 1
  } else {
    result.feedback.push('Password must contain at least one lowercase letter')
  }
  
  if (/[A-Z]/.test(password)) {
    result.score += 1
  } else {
    result.feedback.push('Password must contain at least one uppercase letter')
  }
  
  if (/[0-9]/.test(password)) {
    result.score += 1
  } else {
    result.feedback.push('Password must contain at least one number')
  }
  
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    result.score += 1
  } else {
    result.feedback.push('Password must contain at least one special character')
  }
  
  result.isValid = result.score >= 4 && result.feedback.length === 0
  
  return result
}

/**
 * Format password for display (mask sensitive parts)
 * @param {string} password - Password to format
 * @param {number} showChars - Number of characters to show at start and end
 * @returns {string} Formatted password
 */
export const formatPasswordForDisplay = (password, showChars = 2) => {
  if (!password || password.length <= showChars * 2) {
    return password
  }
  
  const start = password.substring(0, showChars)
  const end = password.substring(password.length - showChars)
  const middle = '*'.repeat(password.length - showChars * 2)
  
  return `${start}${middle}${end}`
}

export default {
  generateTemporaryPassword,
  generateSimpleTemporaryPassword,
  validatePasswordStrength,
  formatPasswordForDisplay
}
